import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, CalendarDays, Clock, ArrowRight, ArrowLeft, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { getServices, getSlots, createBooking, verifyPayment } from "../lib/api";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Book() {
  const [params] = useSearchParams();
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState(params.get("service") || "");
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState({ available: [], booked: [], all_slots: [] });
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // {mode:'paid'|'request'}

  useEffect(() => {
    window.scrollTo(0, 0);
    getServices().then((d) => {
      setServices(d);
      if (!serviceId && params.get("service")) setServiceId(params.get("service"));
    });
  }, []);

  const service = useMemo(() => services.find((s) => s.id === serviceId), [services, serviceId]);
  const dateStr = date ? format(date, "yyyy-MM-dd") : "";

  useEffect(() => {
    if (!dateStr) return;
    setSlot("");
    getSlots(dateStr).then(setSlots).catch(() => {});
  }, [dateStr]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canNext =
    (step === 1 && serviceId) ||
    (step === 2 && date && slot) ||
    step === 3;

  const handlePay = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      const res = await createBooking({
        service_id: serviceId,
        name: form.name,
        email: form.email,
        phone: form.phone,
        date: dateStr,
        slot,
        message: form.message,
      });

      if (!res.payment_configured) {
        setDone({ mode: "request" });
        setLoading(false);
        return;
      }

      const ok = await loadRazorpay();
      if (!ok) {
        toast.error("Could not load payment gateway. Please try again.");
        setLoading(false);
        return;
      }

      const rzp = new window.Razorpay({
        key: res.key_id,
        amount: res.amount,
        currency: res.currency,
        order_id: res.order_id,
        name: "Dr. Zeenal Punamiya",
        description: res.service_title,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#D4AF37" },
        handler: async (r) => {
          try {
            await verifyPayment({
              booking_id: res.booking_id,
              razorpay_order_id: r.razorpay_order_id,
              razorpay_payment_id: r.razorpay_payment_id,
              razorpay_signature: r.razorpay_signature,
            });
            setDone({ mode: "paid" });
          } catch (e) {
            toast.error("Payment verification failed. Please contact us.");
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.open();
      setLoading(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="pt-40 pb-24 min-h-screen flex items-center justify-center px-6" data-testid="booking-success">
        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          className="max-w-lg text-center rounded-3xl border border-gold/20 bg-card p-12">
          <div className="w-20 h-20 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="text-gold" size={40} />
          </div>
          <h1 className="font-serif text-4xl text-foreground">
            {done.mode === "paid" ? "Consultation Confirmed" : "Request Received"}
          </h1>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            {done.mode === "paid"
              ? `Thank you, ${form.name}. Your ${service?.title} on ${date && format(date, "PPP")} at ${slot} is confirmed. A confirmation will follow at ${form.email}.`
              : `Thank you, ${form.name}. Your request for a ${service?.title} on ${date && format(date, "PPP")} at ${slot} has been recorded. Dr. Zeenal will reach out at ${form.email} to confirm and arrange payment.`}
          </p>
          <Link to="/" className="mt-10 inline-flex px-8 py-4 rounded-full bg-gold text-ink text-sm uppercase tracking-[0.12em] font-semibold hover:bg-gold-light transition-colors">
            Back to Home
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="book-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <span className="text-xs uppercase tracking-[0.25em] text-gold">Book a Consultation</span>
          <h1 className="font-serif text-5xl md:text-7xl font-light mt-4 leading-none text-foreground">
            Reserve your <span className="text-gradient-gold italic">session.</span>
          </h1>
        </div>

        {/* stepper */}
        <div className="mt-12 flex items-center gap-4">
          {["Service", "Date & Time", "Details"].map((label, i) => (
            <div key={label} className="flex items-center gap-4">
              <div className={`flex items-center gap-2 ${step >= i + 1 ? "text-gold" : "text-muted-foreground"}`}>
                <span className={`w-8 h-8 rounded-full border flex items-center justify-center text-sm ${step > i + 1 ? "bg-gold text-ink border-gold" : step === i + 1 ? "border-gold" : "border-muted-foreground/40"}`}>
                  {step > i + 1 ? <Check size={15} /> : i + 1}
                </span>
                <span className="text-xs uppercase tracking-[0.12em] hidden sm:block">{label}</span>
              </div>
              {i < 2 && <span className="h-px w-8 md:w-16 bg-gold/20" />}
            </div>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* main */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {services.map((s) => (
                    <button key={s.id} onClick={() => setServiceId(s.id)} data-testid={`select-service-${s.id}`}
                      className={`text-left rounded-2xl border p-6 transition-colors duration-300 ${serviceId === s.id ? "border-gold bg-gold/5" : "border-gold/15 bg-card hover:border-gold/40"}`}>
                      <div className="flex items-baseline justify-between mb-2">
                        <span className="text-xs uppercase tracking-[0.12em] text-gold/70">{s.duration}</span>
                        <span className="font-serif text-2xl text-gradient-gold">₹{s.price.toLocaleString("en-IN")}</span>
                      </div>
                      <h3 className="font-serif text-xl text-foreground">{s.title}</h3>
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.tagline}</p>
                    </button>
                  ))}
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="rounded-2xl border border-gold/15 bg-card p-4 flex justify-center">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0}
                      data-testid="booking-calendar"
                    />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-gold mb-4 flex items-center gap-2">
                      <Clock size={14} /> {date ? `Slots for ${format(date, "PPP")}` : "Select a date first"}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {(slots.all_slots || []).map((s) => {
                        const taken = slots.booked?.includes(s);
                        return (
                          <button key={s} disabled={!date || taken} onClick={() => setSlot(s)} data-testid={`slot-${s.replace(/[:\s]/g, "")}`}
                            className={`px-4 py-3 rounded-lg border text-sm transition-colors duration-300 disabled:opacity-30 disabled:cursor-not-allowed ${slot === s ? "border-gold bg-gold text-ink font-semibold" : "border-gold/20 text-foreground hover:border-gold/50"}`}>
                            {s}
                          </button>
                        );
                      })}
                    </div>
                    {date && slots.available?.length === 0 && (
                      <p className="text-sm text-muted-foreground mt-4">No slots left on this day. Please pick another date.</p>
                    )}
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                  className="rounded-2xl border border-gold/15 bg-card p-8 space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Full Name</label>
                      <input value={form.name} onChange={update("name")} data-testid="booking-name"
                        className="mt-2 w-full bg-input/40 border border-gold/15 rounded-lg px-4 py-3 text-foreground focus:border-gold outline-none" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone</label>
                      <input value={form.phone} onChange={update("phone")} data-testid="booking-phone"
                        className="mt-2 w-full bg-input/40 border border-gold/15 rounded-lg px-4 py-3 text-foreground focus:border-gold outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</label>
                    <input type="email" value={form.email} onChange={update("email")} data-testid="booking-email"
                      className="mt-2 w-full bg-input/40 border border-gold/15 rounded-lg px-4 py-3 text-foreground focus:border-gold outline-none" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">What would you like to discuss?</label>
                    <textarea rows={4} value={form.message} onChange={update("message")} data-testid="booking-message"
                      className="mt-2 w-full bg-input/40 border border-gold/15 rounded-lg px-4 py-3 text-foreground focus:border-gold outline-none resize-none" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* nav buttons */}
            <div className="mt-8 flex items-center justify-between">
              <button
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                disabled={step === 1}
                data-testid="booking-back"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-gold/20 text-muted-foreground text-sm uppercase tracking-[0.12em] disabled:opacity-30 hover:text-gold hover:border-gold/50 transition-colors">
                <ArrowLeft size={16} /> Back
              </button>
              {step < 3 ? (
                <button
                  onClick={() => canNext && setStep((s) => s + 1)}
                  disabled={!canNext}
                  data-testid="booking-next"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gold text-ink text-sm uppercase tracking-[0.12em] font-semibold hover:bg-gold-light transition-colors disabled:opacity-40">
                  Continue <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  onClick={handlePay}
                  disabled={loading}
                  data-testid="booking-pay"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gold text-ink text-sm uppercase tracking-[0.12em] font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <>Pay & Confirm ₹{service?.price?.toLocaleString("en-IN")}</>}
                </button>
              )}
            </div>
          </div>

          {/* summary */}
          <div className="lg:col-span-4">
            <div className="sticky top-28 rounded-2xl border border-gold/20 bg-card p-7" data-testid="booking-summary">
              <h3 className="font-serif text-2xl text-foreground mb-6">Your Booking</h3>
              <div className="space-y-4 text-sm">
                <SummaryRow label="Service" value={service?.title || "—"} />
                <SummaryRow label="Duration" value={service?.duration || "—"} />
                <SummaryRow label="Date" value={date ? format(date, "PPP") : "—"} icon={<CalendarDays size={14} />} />
                <SummaryRow label="Time" value={slot || "—"} icon={<Clock size={14} />} />
              </div>
              <div className="mt-6 pt-6 border-t border-gold/15 flex items-baseline justify-between">
                <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Total</span>
                <span className="font-serif text-3xl text-gradient-gold">
                  {service ? `₹${service.price.toLocaleString("en-IN")}` : "—"}
                </span>
              </div>
              <p className="mt-5 flex items-start gap-2 text-xs text-muted-foreground">
                <Info size={14} className="text-gold shrink-0 mt-0.5" />
                Secure payment via Razorpay. Your slot is held while you complete checkout.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const SummaryRow = ({ label, value, icon }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-muted-foreground flex items-center gap-2">{icon}{label}</span>
    <span className="text-foreground text-right font-medium">{value}</span>
  </div>
);
